import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { X } from "lucide-react";
import "../../styles/tailwind.css";

/**
 * Centered panel below the app header (not pinned to top of viewport).
 * Tailwind z-index + layout; inner body keeps Bootstrap row/col split.
 */
export default function HeaderPanelModal({
  open,
  onClose,
  title,
  subtitle,
  headerIcon,
  panelRef,
  footer = null,
  children,
  ariaLabelledBy,
  panelClassName = "",
  compact = false,
  headerClassName = "",
  headerSubtitleClassName = "mb-0 truncate text-[11px] text-white/75",
  headerTone = "brand",
}) {
  const isLightHeader = headerTone === "light";
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="tailwind-container">
      <div
        className="fixed inset-0 !z-[10040] bg-slate-900/60 backdrop-blur-sm"
        style={{ zIndex: 10040 }}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="fixed inset-0 !z-[10050] overflow-y-auto overscroll-contain"
        style={{ zIndex: 10050 }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        onClick={onClose}
      >
        <div
          className={`flex min-h-full items-start justify-center px-4 pb-10 pt-20 sm:px-6 sm:pt-24 sm:pb-12 ${panelClassName}`}
        >
          <div
            ref={panelRef}
            className={`pointer-events-auto flex min-w-[320px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_28px_56px_-14px_rgba(15,23,42,0.4)] ring-1 ring-slate-900/5 ${
              compact ? "w-[min(94vw,440px)]" : "w-[min(94vw,58vw)]"
            }`}
            style={
              compact
                ? { height: "auto", maxHeight: "min(520px, calc(100vh - 7.5rem))" }
                : {
                    height: "min(720px, calc(100vh - 7.5rem))",
                    maxHeight: "min(720px, calc(100vh - 7.5rem))",
                  }
            }
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isLightHeader ? (
              <header
                className={`cms-panel-header-pro ${headerClassName}`.trim()}
              >
                <h2 id={ariaLabelledBy} className="cms-panel-header-pro__title">
                  {title}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="cms-panel-header-pro__close"
                  aria-label="Close"
                >
                  <X strokeWidth={2.25} aria-hidden />
                </button>
              </header>
            ) : (
              <div
                className={`cms-panel-header flex shrink-0 items-center justify-between gap-2 border-0 px-4 py-2.5 bg-gradient-to-r from-[#0b2166] via-[#1a3a8a] to-[#1e40af] ${headerClassName}`}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  {headerIcon ? (
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15 text-sm text-white"
                      aria-hidden
                    >
                      {headerIcon}
                    </span>
                  ) : null}
                  <div className="min-w-0">
                    <h2
                      id={ariaLabelledBy}
                      className="mb-0 truncate text-sm font-semibold text-white"
                    >
                      {title}
                    </h2>
                    {subtitle ? (
                      <p className={headerSubtitleClassName}>{subtitle}</p>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="cms-panel-close"
                  aria-label="Close"
                >
                  <FaTimes aria-hidden />
                </button>
              </div>
            )}

            <div className="cms-panel-body flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50/40 p-0">
              {children}
            </div>

            {footer}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function formatRelativeTime(dateString) {
  if (!dateString) return "Now";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Original split: Bootstrap row + col-md-5 / col-md-7 */
export function MessagingSplitGrid({ children }) {
  return <div className="cms-panel-split row g-0 h-100 min-h-0 flex-1">{children}</div>;
}

export function MessagingListColumn({ toolbar, children }) {
  return (
    <div className="cms-panel-list col-md-5 flex min-h-0 flex-col overflow-hidden border-end border-slate-200 bg-slate-100/80 p-0">
      {toolbar}
      <div className="cms-panel-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain p-2.5">
        {children}
      </div>
    </div>
  );
}

export function MessagingListToolbar({ title, trailing }) {
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2"
    >
      <span className="text-xs font-semibold text-slate-800">{title}</span>
      {trailing}
    </div>
  );
}

export function MessagingDetailColumn({ children }) {
  return (
    <div className="cms-panel-detail col-md-7 min-h-0 bg-white p-0">
      <div className="cms-panel-detail-inner flex h-full min-h-0 flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );
}

export function PanelListSkeleton({ rows = 6 }) {
  return (
    <div className="space-y-3 p-2">
      {[...Array(rows)].map((_, idx) => (
        <div
          key={idx}
          className="flex animate-pulse gap-3 rounded-xl bg-white p-3"
        >
          <div className="h-10 w-10 shrink-0 rounded-full bg-slate-200" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-4/5 rounded bg-slate-200" />
            <div className="h-2 w-1/2 rounded bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function PanelEmptyState({ icon, title, description }) {
  return (
    <div
      className="flex h-full min-h-[200px] flex-col items-center justify-center px-6 py-8 text-center"
    >
      <div
        className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white text-xl text-slate-400 shadow-sm"
      >
        {icon}
      </div>
      <h6 className="mb-1 text-sm font-semibold text-slate-700">{title}</h6>
      <p className="mb-0 max-w-[240px] text-xs leading-relaxed text-slate-500">
        {description}
      </p>
    </div>
  );
}

export function messagingListCardClass(isActive, isUnread) {
  return [
    "w-full rounded-xl border p-3 text-left transition-all duration-150",
    isActive
      ? "border-blue-300 bg-white shadow-md ring-2 ring-blue-500/20"
      : "border-transparent bg-white/80 hover:border-slate-200 hover:bg-white hover:shadow-sm",
    isUnread && !isActive ? "bg-blue-50/90" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export const panelBadgeTw = {
  primary:
    "rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800",
  info:
    "rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-900",
  warning:
    "rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900",
  secondary:
    "rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700",
  success:
    "rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800",
  danger:
    "rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800",
};
